import { useInfiniteQuery } from '@tanstack/react-query';
import { getOffsetPageParam, unwrap } from '@billda/api';
import { api } from '../../../shared/api/billdaApi';
import { useAuthStore } from '../../auth/stores/authStore';
import type { LabourItemListItem } from '@billda/api';

export const labourItemsKeys = {
  all: ['labourItems'] as const,
  lists: () => [...labourItemsKeys.all, 'list'] as const,
  list: (siteId: number, plotId: number, isAuthenticated: boolean) =>
    [...labourItemsKeys.lists(), { siteId, plotId, isAuthenticated }] as const,
};

const ITEMS_PER_PAGE = 20;

export interface UseLabourItemsQueryReturn {
  labourItems: LabourItemListItem[];
  totalCount: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: Error | null;
  loadMore: () => void;
}

export function useLabourItemsQuery(
  siteId: number,
  plotId: number,
  enabled: boolean = true
): UseLabourItemsQueryReturn {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useInfiniteQuery({
    queryKey: labourItemsKeys.list(siteId, plotId, isAuthenticated),
    queryFn: async ({ pageParam = 0, signal }) => {
      return unwrap(
        await api.legacy.GET(
          '/api/v2/sites/{siteId}/plots/{plotId}/labour-items',
          {
            params: {
              path: { siteId, plotId },
              query: { offset: pageParam, take: ITEMS_PER_PAGE },
            },
            signal,
          }
        )
      );
    },
    getNextPageParam: getOffsetPageParam,
    initialPageParam: 0,
    enabled: isAuthenticated && siteId > 0 && plotId > 0 && enabled,
    staleTime: 30000,
    placeholderData: previousData => previousData,
  });

  const labourItems = data?.pages.flatMap(page => page?.items ?? []) ?? [];
  const totalCount = data?.pages[0]?.totalCount ?? 0;

  return {
    labourItems,
    totalCount,
    hasMore: hasNextPage ?? false,
    isLoading,
    isLoadingMore: isFetchingNextPage,
    error: error as Error | null,
    loadMore: fetchNextPage,
  };
}
